<p-drawer
  [visible]="visible()"
  (visibleChange)="visibleChange.emit($event)"
  position="right"
  [style]="{ width: '560px' }"
  [modal]="true"
  (onHide)="close()">

  <ng-template pTemplate="header">
    <div class="drawer-header">
      <i class="pi pi-server custom-sip-icon"></i>
      <h2 i18n="@@customSip.drawer.title">Connect phone number using Custom SIP</h2>
    </div>
  </ng-template>

  <div
    class="drawer-content"
    snAssistantSurface="importNumber"
    i18n-surfaceTitle="@@importNumber.surface"
    surfaceTitle="Connect a phone number">

    @if (createdAccount()) {
      <p-message severity="success" styleClass="gateway-msg" [closable]="false">
        <div class="gateway-msg-body">
          <div class="gateway-title" i18n="@@customSip.gateway.title">
            Account created. Configure your SIP provider with these details:
          </div>
          <app-sip-gateway-info [info]="createdGateway()" />
        </div>
      </p-message>
    }

    @if (!showAccountForm()) {
      <!-- Account picker / create -->
      <div class="form-field">
        <label class="field-label" i18n="@@customSip.field.account">Custom SIP account</label>
        @if (customAccountOptions().length > 0) {
          <p-select
            [ngModel]="selectedAccountId()"
            (ngModelChange)="onSelectedAccountChange($event)"
            [snAssistantId]="F.account"
            snAssistantLabel="Custom SIP account"
            [options]="customAccountOptions()"
            i18n-placeholder="@@customSip.field.accountPlaceholder"
            placeholder="Select an existing Custom SIP account"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full">
          </p-select>
        }
        <p-button
          icon="pi pi-plus"
          severity="secondary"
          [outlined]="true"
          size="small"
          styleClass="add-account-btn"
          i18n-label="@@customSip.btn.addAccount"
          label="Add Custom SIP account"
          (click)="openCreateAccount()">
        </p-button>
      </div>

      <!-- Phone number to bind -->
      <div class="form-field">
        <label for="label" class="field-label" i18n="@@phoneNumbers.field.label">Label</label>
        <input
          pInputText
          id="label"
          [ngModel]="label()"
          (ngModelChange)="onLabelInput($event)"
          [snAssistantId]="F.label"
          i18n-placeholder="@@phoneNumbers.field.labelPlaceholder"
          placeholder="Easy to identify name of the phone number"
          class="w-full" />
      </div>

      <div class="form-field">
        <label for="phoneNumber" class="field-label" i18n="@@customSip.field.phoneNumber">Phone number (E.164)</label>
        <input
          pInputText
          id="phoneNumber"
          [ngModel]="phoneNumber()"
          (ngModelChange)="onPhoneNumberInput($event)"
          [snAssistantId]="F.phoneNumber"
          placeholder="+71234567890"
          class="w-full"
          [class.p-invalid]="phoneNumberInvalid()" />
        @if (phoneNumberInvalid()) {
          <small class="field-error" i18n="@@customSip.err.e164">
            Must be E.164 format: + followed by 1–15 digits.
          </small>
        }
      </div>
    }

    @if (showAccountForm()) {
      @if (gatewayInfo()) {
        <p-message severity="info" styleClass="gateway-msg" [closable]="false">
          <div class="gateway-msg-body">
            <div class="gateway-title" i18n="@@customSip.gateway.previewTitle">
              Configure your SIP provider with these details from our side:
            </div>
            <app-sip-gateway-info [info]="gatewayInfo()" />
          </div>
        </p-message>
      }

      <p-card styleClass="account-form-card">
        <div class="form-field">
          <label for="acct-name" class="field-label">
            <span i18n="@@customSip.field.name">Account name</span>
            <span class="required-marker">*</span>
          </label>
          <input
            pInputText
            id="acct-name"
            [formControl]="f.name"
            [snAssistantId]="F.name"
            i18n-placeholder="@@customSip.field.namePlaceholder"
            placeholder="e.g. Mango Office RU"
            class="w-full"
            [class.p-invalid]="isInvalid('name')" />
        </div>

        <div class="form-field">
          <label for="acct-server" class="field-label">
            <span i18n="@@customSip.field.server">SIP server address</span>
          </label>
          <input
            pInputText
            id="acct-server"
            [formControl]="f.sipServerAddress"
            [snAssistantId]="F.sipServerAddress"
            placeholder="sip.mango.com:5060"
            class="w-full"
            i18n-pTooltip="@@customSip.tooltip.server"
            [pTooltip]="'host[:port] of YOUR SIP provider (Mango etc.) — not ' + appName + '. Leave empty to disable outbound.'"
            tooltipPosition="top" />
        </div>

        <div class="form-field">
          <label class="field-label">
            <span i18n="@@customSip.field.transport">SIP transport</span>
            <span class="required-marker">*</span>
          </label>
          <p-select
            [formControl]="f.sipTransport"
            [snAssistantId]="F.sipTransport"
            [options]="transportOptions"
            styleClass="w-full">
          </p-select>
        </div>

        <div class="form-field">
          <label for="acct-country" class="field-label">
            <span i18n="@@customSip.field.country">Destination country</span>
            <span class="required-marker">*</span>
          </label>
          <input
            pInputText
            id="acct-country"
            [formControl]="f.sipDestinationCountry"
            [snAssistantId]="F.sipDestinationCountry"
            placeholder="RU"
            maxlength="2"
            class="w-full short-input"
            i18n-pTooltip="@@customSip.tooltip.country"
            [pTooltip]="'ISO-3166 α-2 (2 letters). Hint for ' + appName + ' billing/routing — NOT a destination restriction; you can still call any country.'"
            tooltipPosition="top"
            [class.p-invalid]="isInvalid('sipDestinationCountry')" />
          @if (isInvalid('sipDestinationCountry')) {
            <small class="field-error" i18n="@@customSip.err.country">
              Must be 2 uppercase letters (e.g. RU, US).
            </small>
          }
        </div>

        <div class="form-field">
          <label for="acct-allowed" class="field-label">
            <span i18n="@@customSip.field.allowedAddresses">Allowed inbound IPs</span>
            <span class="required-marker">*</span>
          </label>
          <input
            pInputText
            id="acct-allowed"
            [formControl]="f.sipAllowedAddresses"
            [snAssistantId]="F.sipAllowedAddresses"
            placeholder="1.2.3.4, 10.0.0.0/24"
            class="w-full"
            i18n-pTooltip="@@customSip.tooltip.allowedAddresses"
            [pTooltip]="'Comma- or space-separated list of IPv4 or IPv4/CIDR. IPs of YOUR SIP provider from which ' + appName + ' will accept inbound INVITE — not your own.'"
            tooltipPosition="top"
            [class.p-invalid]="allowedAddressesInvalid().length > 0" />
          @if (allowedAddressesInvalid().length > 0) {
            <small class="field-error">
              <span i18n="@@customSip.err.invalidCidr">Invalid IPv4 / CIDR:</span>
              {{ allowedAddressesInvalid().join(', ') }}
            </small>
          }
        </div>

        <div class="auth-section">
          <div class="auth-section-title">Outbound auth ({{ appName }} → provider, optional)</div>
          <div class="auth-section-hint" i18n="@@customSip.section.outboundHint">
            Set both for Digest auth, or leave both empty for IP-trust mode (Mango SIP-trunk, Plivo, etc.).
          </div>

          <div class="form-field">
            <label for="acct-out-user" class="field-label" i18n="@@customSip.field.outboundAuthUsername">Outbound SIP username</label>
            <input
              pInputText
              id="acct-out-user"
              [formControl]="f.sipOutboundAuthUsername"
            [snAssistantId]="F.sipOutboundAuthUsername"
              class="w-full"
              i18n-pTooltip="@@customSip.tooltip.outboundAuthPair"
              pTooltip="Either set BOTH username and password (Digest auth), or leave BOTH empty (IP-trust mode)."
              tooltipPosition="top" />
          </div>

          <div class="form-field">
            <label for="acct-out-pwd" class="field-label" i18n="@@customSip.field.outboundAuthPassword">Outbound SIP password</label>
            <input
              pInputText
              id="acct-out-pwd"
              type="password"
              [formControl]="f.sipOutboundAuthPassword"
              snAssistantSkip
              class="w-full"
              i18n-pTooltip="@@customSip.tooltip.outboundAuthPair"
              pTooltip="Either set BOTH username and password (Digest auth), or leave BOTH empty (IP-trust mode)."
              tooltipPosition="top" />
          </div>

          @if (hasOutboundAuthPairError()) {
            <p-message
              severity="warn"
              [closable]="false"
              i18n-text="@@customSip.err.outboundAuthPair"
              text="Outbound username and password must be set together, or both left empty.">
            </p-message>
          }
        </div>

        <div class="auth-section">
          <div class="auth-section-title">Inbound auth (provider → {{ appName }}, optional)</div>
          <div class="auth-section-hint" i18n="@@customSip.section.inboundHint">
            Most providers don't challenge inbound — IP allowlist above is the gate. Leave both empty unless your provider requires Digest auth.
          </div>

          <div class="form-field">
            <label for="acct-in-user" class="field-label" i18n="@@customSip.field.inboundAuthUsername">Inbound SIP username</label>
            <input
              pInputText
              id="acct-in-user"
              [formControl]="f.sipInboundAuthUsername"
            [snAssistantId]="F.sipInboundAuthUsername"
              class="w-full"
              i18n-pTooltip="@@customSip.tooltip.inboundAuthPair"
              pTooltip="Either set BOTH username and password, or leave BOTH empty (IP-only inbound auth)."
              tooltipPosition="top" />
          </div>

          <div class="form-field">
            <label for="acct-in-pwd" class="field-label" i18n="@@customSip.field.inboundAuthPassword">Inbound SIP password</label>
            <input
              pInputText
              id="acct-in-pwd"
              type="password"
              [formControl]="f.sipInboundAuthPassword"
              snAssistantSkip
              class="w-full"
              i18n-pTooltip="@@customSip.tooltip.inboundAuthPair"
              pTooltip="Either set BOTH username and password, or leave BOTH empty (IP-only inbound auth)."
              tooltipPosition="top" />
          </div>

          @if (hasInboundAuthPairError()) {
            <p-message
              severity="warn"
              [closable]="false"
              i18n-text="@@customSip.err.inboundAuthPair"
              text="Inbound username and password must be set together, or both left empty.">
            </p-message>
          }
        </div>

        <div class="card-actions">
          <p-button
            severity="secondary"
            [outlined]="true"
            size="small"
            (click)="cancelCreateAccount()"
            [disabled]="isCreatingAccount()"
            i18n-label="@@common.cancel"
            label="Cancel">
          </p-button>
          <p-button
            size="small"
            [icon]="isCreatingAccount() ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
            (click)="createAccount()"
        snAssistantAction="createAccount"
        actionHint="Creates the SIP account from the form. The passwords in it are filled by a person, not by you."

            [disabled]="accountForm.invalid || isCreatingAccount()"
            [label]="createBtnLabel">
          </p-button>
        </div>
      </p-card>
    }

  </div>

  <ng-template pTemplate="footer">
    <div class="drawer-footer">
      <p-button
        severity="secondary"
        [outlined]="true"
        (click)="close()"
        [disabled]="isImporting()"
        i18n-label="@@common.cancel"
        label="Cancel">
      </p-button>
      <p-button
        [icon]="isImporting() ? 'pi pi-spin pi-spinner' : 'pi pi-link'"
        (click)="importNumber()"
        snAssistantAction="import"
        actionHint="Connects the number to the workspace over the chosen SIP account."

        [disabled]="!canImport()"
        [loading]="isImporting()"
        i18n-label="@@customSip.btn.connect"
        label="Connect">
      </p-button>
    </div>
  </ng-template>
</p-drawer>
